import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollAreaViewport } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Search, ChevronLeft } from "lucide-react";
import { cn } from "@/lib/utils";

// Re-use the same mock data shape
export const mockCompanyData = [
  {
    id: '1',
    name: 'Acme Corporation',
    currency: 'GBP',
    totalAR: 450000,
    overdue: 125000,
    overduePercent: 27.8,
    dso: 45,
    cei: 87,
    avgDaysToPay: 42,
    arTrend: '+12%',
    overdueTrend: '+8%',
    dsoTrend: '-3 days',
    ceiTrend: '+5%',
    paymentSpeed: 'Improving',
  },
  {
    id: '2',
    name: 'Global Industries Ltd',
    currency: 'EUR',
    totalAR: 680000,
    overdue: 95000,
    overduePercent: 14.0,
    dso: 38,
    cei: 92,
    avgDaysToPay: 35,
    arTrend: '+5%',
    overdueTrend: '-2%',
    dsoTrend: '-5 days',
    ceiTrend: '+3%',
    paymentSpeed: 'Improving',
  },
  {
    id: '3',
    name: 'Tech Innovations Inc',
    currency: 'USD',
    totalAR: 320000,
    overdue: 180000,
    overduePercent: 56.3,
    dso: 72,
    cei: 65,
    avgDaysToPay: 68,
    arTrend: '+18%',
    overdueTrend: '+25%',
    dsoTrend: '+12 days',
    ceiTrend: '-8%',
    paymentSpeed: 'Declining',
  },
  {
    id: '4',
    name: 'Retail Solutions Co',
    currency: 'GBP',
    totalAR: 540000,
    overdue: 75000,
    overduePercent: 13.9,
    dso: 32,
    cei: 94,
    avgDaysToPay: 30,
    arTrend: '+3%',
    overdueTrend: '-5%',
    dsoTrend: '-2 days',
    ceiTrend: '+2%',
    paymentSpeed: 'Stable',
  },
];

interface CompanyDetailsSidebarProps {
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function CompanyDetailsSidebar({
  isCollapsed = false,
  onToggleCollapse,
}: Readonly<CompanyDetailsSidebarProps>) {
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const [searchQuery, setSearchQuery] = useState("");

  const filteredCompanies = mockCompanyData.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCompanyClick = (id: string) => {
    navigate(`/analytics-reporting/multi-company/company/${id}`);
  };

  const getCurrencyVariant = (currency: string) => {
    if (currency === 'GBP') return 'secondary';
    if (currency === 'EUR') return 'default';
    return 'outline';
  };

  const getOverdueColor = (percent: number) => {
    if (percent >= 30) return 'text-red-600';
    if (percent >= 20) return 'text-orange-600';
    return 'text-green-600';
  };

  return (
    <div className="relative h-full flex flex-col">
      {!isCollapsed && (
        <Card className="h-full border-r rounded-none flex flex-col shadow-md">
          <CardHeader className="pb-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Companies</CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleCollapse}
                className="h-8 w-8 rounded-full hover:bg-muted"
                title="Close sidebar"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>
            <div className="relative mt-2">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search companies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-9"
              />
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-0 flex-1 min-h-0">
            <ScrollArea className="h-full w-full">
              <ScrollAreaViewport className="h-full">
                <div className="space-y-1 px-3 pb-3">
                  {filteredCompanies.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      No companies found
                    </div>
                  )}
                  {filteredCompanies.map((company) => (
                    <button
                      key={company.id}
                      onClick={() => handleCompanyClick(company.id)}
                      className={cn(
                        "w-full text-left p-3 rounded-lg border-l-4 transition-all hover:bg-muted/50",
                        company.id === companyId
                          ? "border-l-teal-500 bg-primary/10 shadow-sm"
                          : "border-l-gray-300 bg-background hover:shadow-sm"
                      )}
                    >
                      <div className="space-y-1.5">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-semibold text-sm truncate flex-1">{company.name}</p>
                          <Badge variant={getCurrencyVariant(company.currency)} className="text-xs shrink-0 font-mono">
                            {company.currency}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground">DSO: {company.dso} days</span>
                          <span className={cn("text-xs font-medium", getOverdueColor(company.overduePercent))}>
                            {company.overduePercent.toFixed(1)}% overdue
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollAreaViewport>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
